import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../logo.png';
import ThemeToggle from './ThemeToggle';

const BRAND_BLUE = '#14a9e8';
const BRAND_INK = '#0b1620';

const Header = () => {
  const location = useLocation();
  const navigate  = useNavigate();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('sm'));

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const navItems = [
    { to: '/',         label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/about',    label: 'About' },
    { to: '/faq',      label: 'FAQs' },
    { to: '/blog',     label: 'Blog' },
    { to: '/quote',    label: 'Quote' },
  ];

  const isActive = (path) => location.pathname === path;
  const handleMenuOpen  = (e) => setMenuAnchorEl(e.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const quoteBtnSx = {
    bgcolor: BRAND_BLUE, color: '#fff',
    '&:hover': { bgcolor: '#1298cf' },
    textTransform: 'none', fontWeight: 700,
  };
  const navBtnSx = (active) => ({
    textTransform: 'none', fontWeight: 700,
    color: active ? 'text.primary' : 'text.secondary',
    borderColor: `${BRAND_BLUE}55`,
    ...(active ? { bgcolor: 'rgba(20,169,232,.12)', borderColor: `${BRAND_BLUE}88` } : { bgcolor: 'transparent' }),
    '&:hover': { bgcolor: 'rgba(20,169,232,.08)' },
  });

  return (
    <>
      {/* TOP BANNER */}
      <Box sx={{ bgcolor: BRAND_BLUE, color: '#fff' }}>
        <Box
          sx={{
            maxWidth: 1200, mx: 'auto', px: 2, py: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="body2" fontWeight={700}
            sx={{ display: { xs: 'none', sm: 'inline' } }}
          >
            Spotless results. Professional finish. Honest service.
          </Typography>
          <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
            <Button
              onClick={() => navigate('/quote')}
              size="small"
              variant="outlined"
              sx={{
                color:'#fff', borderColor:'rgba(255,255,255,.6)',
                '&:hover':{ borderColor:'#fff', bgcolor:'rgba(255,255,255,.14)' },
                width: { xs: '100%', sm: 'auto' },
                maxWidth: { xs: 340, sm: 'none' },
              }}
            >
              Get a Free Quote
            </Button>
          </Box>
        </Box>
      </Box>

      <AppBar
        position="sticky"
        elevation={0}
        sx={(theme) => ({
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(18,18,18,.92)' : 'rgba(255,255,255,.92)',
          backdropFilter: 'blur(6px)',
          borderBottom: '1px solid rgba(0,0,0,.08)',
        })}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 60, sm: 72 },
            px: { xs: 1.5, sm: 2 },
            maxWidth: 1200, width: '100%', mx: 'auto',
            gap: 1,
            overflow: 'hidden',
          }}
        >
          {/* LEFT: logo + business name */}
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              textDecoration: 'none', color: 'text.primary',
              minWidth: 0, flexShrink: 1,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Lowen Behold Cleaning logo"
              sx={{
                width: { xs: 36, sm: 56 }, height: { xs: 36, sm: 56 },
                objectFit: 'contain', flexShrink: 0
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700, letterSpacing: 0.2,
                lineHeight: 1.1,
                fontSize: { xs: '1rem', sm: '1.15rem' },
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
              }}
            >
              Lowen Behold Cleaning LLC.
            </Typography>
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1, minWidth: 8 }} />

          {/* Desktop nav */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {navItems.slice(0, 5).map((item) => (
                <Button key={item.to} component={Link} to={item.to} variant="outlined" size="small" sx={navBtnSx(isActive(item.to))}>
                  {item.label}
                </Button>
              ))}
              <Button onClick={() => navigate('/quote')} variant="contained" size="small" sx={quoteBtnSx}>
                Get a Quote
              </Button>
              <ThemeToggle />
            </Box>
          )}

          {/* Mobile hamburger + theme toggle */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ThemeToggle />
              <IconButton
                edge="end" aria-label="menu" onClick={handleMenuOpen}
                sx={{ color: 'text.primary', ml: 0, flexShrink: 0 }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>

        {/* Mobile menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          PaperProps={{
            sx: {
              mt: 1, borderRadius: 2, border: '1px solid rgba(0,0,0,.08)',
              maxWidth: 'calc(100vw - 16px)',
            },
          }}
        >
          {navItems.map((item) => (
            <MenuItem
              key={item.to}
              component={Link}
              to={item.to}
              onClick={handleMenuClose}
              selected={isActive(item.to)}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </AppBar>
    </>
  );
};

export default Header;
